import { WelcomeVideoType } from '../value-objects/WelcomeVideoType';
import { TargetRole } from '../value-objects/TargetRole';

/**
 * WelcomeVideo Entity
 * Represents a welcome or announcement video shown on the dashboard
 */
export interface WelcomeVideoProps {
  id: number;
  workspaceId: number;
  cycleId?: number | null;
  cycleName?: string | null;
  targetRole: TargetRole;
  videoType: WelcomeVideoType;
  videoUrl: string;
  title: string;
  description?: string | null;
  thumbnailUrl?: string | null;
  displayOrder: number;
  isActive: boolean;
  showOnce: boolean;
  scheduledAt?: Date | null;
  expiresAt?: Date | null;
  createdBy?: string | null;
  createdAt: Date;
  updatedAt: Date;
  // View status (for current user)
  isViewed?: boolean;
  viewedAt?: Date | null;
  completed?: boolean;
}

export class WelcomeVideo {
  private readonly props: WelcomeVideoProps;

  constructor(props: WelcomeVideoProps) {
    this.props = props;
  }

  get id(): number {
    return this.props.id;
  }

  get workspaceId(): number {
    return this.props.workspaceId;
  }

  get cycleId(): number | null {
    return this.props.cycleId ?? null;
  }

  get cycleName(): string | null {
    return this.props.cycleName ?? null;
  }

  get targetRole(): TargetRole {
    return this.props.targetRole;
  }

  get videoType(): WelcomeVideoType {
    return this.props.videoType;
  }

  get videoUrl(): string {
    return this.props.videoUrl;
  }

  get title(): string {
    return this.props.title;
  }

  get description(): string | null {
    return this.props.description ?? null;
  }

  get thumbnailUrl(): string | null {
    return this.props.thumbnailUrl ?? null;
  }

  get displayOrder(): number {
    return this.props.displayOrder;
  }

  get isActive(): boolean {
    return this.props.isActive;
  }

  get showOnce(): boolean {
    return this.props.showOnce;
  }

  get scheduledAt(): Date | null {
    return this.props.scheduledAt ?? null;
  }

  get expiresAt(): Date | null {
    return this.props.expiresAt ?? null;
  }

  get createdBy(): string | null {
    return this.props.createdBy ?? null;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get updatedAt(): Date {
    return this.props.updatedAt;
  }

  get isViewed(): boolean {
    return this.props.isViewed ?? false;
  }

  get viewedAt(): Date | null {
    return this.props.viewedAt ?? null;
  }

  get completed(): boolean {
    return this.props.completed ?? false;
  }

  /**
   * Check if the video is applicable for a given role
   */
  isApplicableForRole(role: string): boolean {
    return this.targetRole === 'all' || this.targetRole === role;
  }

  /**
   * Check if the video is applicable for a given cycle
   */
  isApplicableForCycle(cycleId: number | null): boolean {
    // If no cycle is specified, it's applicable to all cycles
    if (this.cycleId === null) return true;
    // If a cycle is specified, it must match
    return this.cycleId === cycleId;
  }

  /**
   * Check if the video is currently scheduled to be shown
   */
  isScheduledNow(): boolean {
    const now = new Date();

    // Check if scheduled_at has passed
    if (this.scheduledAt && this.scheduledAt > now) {
      return false;
    }

    // Check if expires_at has not passed
    if (this.expiresAt && this.expiresAt <= now) {
      return false;
    }

    return true;
  }

  /**
   * Check if the video should be displayed to the user
   */
  shouldDisplay(userRole: string, currentCycleId: number | null): boolean {
    return (
      this.isActive &&
      this.isApplicableForRole(userRole) &&
      this.isApplicableForCycle(currentCycleId) &&
      this.isScheduledNow() &&
      (!this.showOnce || !this.isViewed)
    );
  }

  toPlainObject(): WelcomeVideoProps {
    return { ...this.props };
  }
}
